<template>
    <div class="sms-text-box-secondary-footer">
        <div class="footer-info">
            <span class="error-message">
                {{ errorMessage }}
            </span>

            <template v-if="characterCount > 0">
                <DsPopover :is-open="isPopoverOpen" @close="isPopoverOpen = false">
                    <template #reference>
                        <LinkButton
                            as="button"
                            class="popover-link"
                            data-qa="character-count"
                            :error="hasError || isMessageTooLong"
                            @click="isPopoverOpen = !isPopoverOpen"
                        >
                            {{ countMessage }}
                        </LinkButton>
                    </template>

                    <template #default>
                        <div class="popover-content">
                            {{ $t('sms.broadcast.secondaryFooter.tooltipContent.textMessageSplitting.prefix') }}
                            <strong>{{ $t('sms.broadcast.secondaryFooter.tooltipContent.textMessageSplitting.root') }}</strong>
                            {{ $t('sms.broadcast.secondaryFooter.tooltipContent.textMessageSplitting.suffix') }}
                            <a
                                :href="$options.KEAP_SMS_COMPLIANCE_URL"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-qa="keap-sms-compliance-anchor"
                            >
                                {{ $t('sms.broadcast.learnMore') }}
                            </a>
                        </div>
                    </template>
                </DsPopover>
            </template>
        </div>

        <SmsBroadcastInlineAlerts
            v-if="characterCount > 0"
            :character-count="characterCount"
            :has-invalid-character="messageHasUnsupportedCharacters"
            :num-sms-segments="numSmsSegments"
            :recipient-count="sourcedBasedRecipientCount"
            :show-total-count-message="!ktvEnabled"
            :show-ktv-too-long-message="ktvEnabled"
        />
    </div>
</template>

<script>
import SmsBroadcastInlineAlerts from '@/components/broadcast/sms/SmsBroadcastInlineAlerts.vue';
import LinkButton from '@/components/shared/LinkButton';
import {
    KEAP_SMS_COMPLIANCE_URL,
    SMS_MESSAGE_LENGTH,
    SOURCES,
} from '@/components/broadcast/constants/smsBroadcast.constants';

export default {
    KEAP_SMS_COMPLIANCE_URL,

    components: {
        LinkButton,
        SmsBroadcastInlineAlerts,
    },

    inject: ['$richTextBox'],

    props: {
        recipientCount: {
            type: Number,
            default: 0,
        },
        numSmsSegments: {
            type: Number,
            default: 0,
        },
        source: String,
        ktvEnabled: {
            type: Boolean,
            default: false,
        },
    },


    data() {
        return {
            isPopoverOpen: false,
        };
    },

    computed: {
        sourcedBasedRecipientCount() {
            // recipient count for automations/campaigns is unknowable; default to 1
            return this.source === SOURCES.AUTOMATION ? 1 : this.recipientCount;
        },

        errorMessage() {
            const errors = [];

            if (this.hasError && this.$richTextBox.characterCount() === 0) {
                errors.push(this.$t('sms.broadcast.secondaryFooter.error.pleaseEnterText'));
            }

            if (this.hasError && this.messageHasUnsupportedCharacters) {
                errors.push(this.$t('sms.broadcast.richTextBox.error.unsupportedCharacters'));
            }

            if (this.isMessageTooLong) {
                const { maxCharacterCount } = this;

                errors.push(this.$t('sms.broadcast.secondaryFooter.error.tooLongError', { count: maxCharacterCount }));
            }

            if (this.$richTextBox.hasUnsupportedMergeField()) {
                errors.push(this.$t('sms.broadcast.secondaryFooter.error.messageHasUnsupportedMergeField'));
            }

            return errors.join(', ');
        },

        hasError() {
            return this.$richTextBox.hasError();
        },

        isMessageTooLong() {
            return this.$richTextBox.isMessageTooLong();
        },

        messageHasUnsupportedCharacters() {
            return this.$richTextBox.hasUnsupportedCharacters();
        },

        characterCount() {
            return this.$richTextBox.characterCount();
        },

        countMessage() {
            const characterCount = `${this.$t('sms.broadcast.secondaryFooter.approx')} ${this.$tc(
                'sms.broadcast.secondaryFooter.characterCount',
                this.characterCount,
                { count: this.characterCount },
            )}`;

            const messageCount = this.$tc('sms.broadcast.secondaryFooter.messageCount', this.numSmsSegments, { count: this.numSmsSegments });

            return this.$t('sms.broadcast.secondaryFooter.countMessage', { characterCount, messageCount });
        },

        maxCharacterCount() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },
    },
};

</script>

<style lang="scss" scoped>

    .footer-info {
        display: flex;
        justify-content: space-between;
        font-size: $font-size-xs;
        padding: px-to-rem(2) 0 $spacing-100;
    }

    .popover-content {
        font-size: $font-size-md;
        line-height: $line-height-md;
        .text-bold {
           font-weight: $font-weight-bold;
        }
    }

    .popover-container {
        --popover-width: #{px-to-rem(284)};
    }

    .popover-link {
        font-weight: normal;
        font-size: $font-size-xs;
    }

    .text-box-footer {
        display: flex;
        justify-content: space-between;
        font-size: $font-size-xs;
        padding-top: px-to-rem(2);
    }

    .error-message {
        color: $color-red;
    }

    .character-count {
        color: $color-text-subtle;

        &.error {
            color: $color-red;
        }
    }
</style>
